import { HttpContext } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { IMetadataConfig, IMetadataData, IMetadataModel } from '@business/api';
import { ENV_CONFIG } from '@business/environment';
import { filterResult, HttpService, IHttpResult, IHttpRouting, IHttpStatus, tapResult } from '@business/http';
import { MetadataStore } from '@business/state';
import { TLink, TProcessGateway } from '@business/types';
import { isUndefined, takeOnce } from '@business/utils';
import { Observable, tap } from 'rxjs';
import { ContextBuilder } from '../../http-context/context-builder.service';
import { NavigationAdapter } from '../../navigation/navigation-adapter.service';

@Injectable({
  providedIn: 'root'
})
export class MetadataService {
  readonly #httpService = inject(HttpService);
  readonly #metadataStore = inject(MetadataStore);
  readonly #navAdapter = inject(NavigationAdapter);

  readonly #url = 'metadata';

  readonly hasMetadata = computed(() => this.#metadataStore.hasMetadata());
  readonly restartUrl = computed(() => this.#metadataStore.startProcessUrl());

  readonly defaultExitUrl = inject(ENV_CONFIG).defaultExitUrl as TLink;

  /**
   * @description
   * detect process value change (defrayal/lead) and update the metadata store with latest gateway value,
   * this logic is triggered upon every http request. whenever a response is received - either successful
   * or errored it is examined to check if the process value has changed or not.
   * @status an http common bll http response status object
   */
  updateProcessGateway({ process }: IHttpStatus): void {
    const { routing } = process;

    if (isUndefined(routing?.nextGateway)) {
      return;
    }

    this.#metadataStore.setProcessGateway(routing?.nextGateway as TProcessGateway);
  }

  /**
   * @description
   * Detects next profile value change and updates the metadata store with the latest profile value.
   * This logic is triggered upon every HTTP request. Whenever a response is received—either successful
   * or errored it is examined to check if the next profile value has changed or not.
   * @routing an HTTP common BLL HTTP response routing object
   */
  updateProcessProfile(routing: IHttpRouting): void {
    this.#metadataStore.setProcessProfile(routing?.nextProfile);
  }

  resetMetadata(): void {
    this.#metadataStore.reset();
  }

  /**
   * resets the session timeout by briefly toggling the signal value
   * the toggle pattern (false → true → false) ensures that any effect watching this signal
   * will trigger, even if multiple resets happen one by one
   */

  loadMetadata(): Observable<IHttpResult<IMetadataModel, IMetadataData, IMetadataConfig>> {
    const options = {
      context: new ContextBuilder().omitGateway().build() as HttpContext
    };
    return this.#httpService.fetch<IMetadataModel, IMetadataData, IMetadataConfig>(this.#url, options).pipe(
      takeOnce(),
      tapResult((result: IHttpResult) => {
        this.#metadataStore.updateFeature(result);
      }),
      filterResult()
    );
  }

  terminateApplication(url: TLink = this.defaultExitUrl): void {
    const context = new ContextBuilder().omitGateway().omitProcessProfile().build(); // the default omitGateway is false; calling this method sets it to true

    this.#httpService
      .delete('exit', undefined, { context })
      .pipe(
        takeOnce(),
        tap(() => this.resetMetadata()),
        tap(() => {
          this.#navAdapter.exitTo(url as TLink);
        })
      )
      .subscribe();
  }

  restartApplication(): void {
    this.terminateApplication(this.restartUrl());
  }
}
